import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { OrderItem } from '../../../types';
import { hasNoPaymentObligation, pendingBalanceOf } from '../../../utils/paymentAmount';
import type { KanbanColumn } from '../hooks/useKanbanOrders';
import { ORDER_STATUSES, ORDER_STATUSES_WRITABLE } from '../constants';

interface Props {
  columns: KanbanColumn[];
  loading: boolean;
  error: string | null;
  /** 落点已按判据放行才调用；返回 promise 时由本组件等它，失败原文就地显示（不再静默回弹） */
  onMoveOrder: (orderId: string, newStatus: string) => void | Promise<unknown>;
  onCardClick: (order: OrderItem) => void;
}

// 各状态列的配色（键为 ../constants 的 ORDER_STATUSES 取值）
const COLUMN_STYLES: Record<string, { header: string; dot: string; count: string }> = {
  '未执行': { header: 'bg-amber-50 border-amber-200',  dot: 'bg-amber-400',  count: 'bg-amber-100 text-amber-600' },
  '执行中': { header: 'bg-cyan-50 border-cyan-200',    dot: 'bg-cyan-400',   count: 'bg-cyan-100 text-cyan-600'   },
  '已完成': { header: 'bg-teal-50 border-teal-200',    dot: 'bg-teal-400',   count: 'bg-teal-100 text-teal-600'   },
  '已取消': { header: 'bg-rose-50 border-rose-200',    dot: 'bg-rose-400',   count: 'bg-rose-100 text-rose-600'   },
  '已归档': { header: 'bg-gray-100 border-gray-200',   dot: 'bg-gray-400',   count: 'bg-gray-200 text-gray-500'   },
};

function fmtDate(d: string | null): string {
  if (!d) return '';
  return d.slice(5).replace('-', '/');
}

function calcTodayArrivalKanban(arrivalDateTime: string | null): { timeStr: string; isPast: boolean; minutesUntil: number } | null {
  if (!arrivalDateTime) return null;
  const arr = new Date(arrivalDateTime);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today.getTime() + 86400000);
  if (arr < today || arr >= todayEnd) return null;
  const now = new Date();
  const minutesUntil = Math.round((arr.getTime() - now.getTime()) / 60000);
  const timeStr = arr.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  const isPast = arr < now;
  return { timeStr, isPast, minutesUntil };
}

// ── Compact kanban card ─────────────────────────────────────────────
function KanbanCard({ order, onClick, isDragging = false }: { order: OrderItem; onClick?: () => void; isDragging?: boolean }) {
  const paidAmt = Number(order.paidAmount ?? 0);
  const totalAmt = Number(order.orderAmount ?? 0);
  const balance = pendingBalanceOf(totalAmt, paidAmt);
  const noPaymentObligation = hasNoPaymentObligation(order.orderAmount, order.paidAmount);
  const todayArrival = calcTodayArrivalKanban(order.arrivalDateTime);
  const isMultiDay = order.orderEndDate && order.orderEndDate !== order.orderStartDate;

  return (
    <div
      onClick={onClick}
      className={`bg-white border rounded-xl p-3 cursor-pointer transition-all select-none ${
        isDragging
          ? 'border-teal-300 rotate-1 opacity-80'
          : todayArrival && !todayArrival.isPast
          ? 'border-rose-200 hover:border-rose-300 hover:bg-rose-50/20 active:scale-[0.98]'
          : 'border-gray-100 hover:border-teal-200 hover:bg-teal-50/30 active:scale-[0.98]'
      }`}
    >
      {/* 今日到达指示条 */}
      {todayArrival && !todayArrival.isPast && (
        <div className="h-0.5 w-full bg-rose-400 rounded-full -mt-3 -mx-3 mb-2 w-[calc(100%+1.5rem)]" style={{ margin: '-0.75rem -0.75rem 0.5rem -0.75rem', borderRadius: '0.75rem 0.75rem 0 0' }} />
      )}

      {/* Order number */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-mono text-gray-400 truncate">
          {order.orderNumber ?? order.id.slice(0,8).toUpperCase()}
        </span>
        {order.serviceType?.name && (
          <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded-full whitespace-nowrap ml-1">
            {order.serviceType.name}
          </span>
        )}
      </div>

      {/* Customer */}
      <div className="flex items-center gap-2 mb-2">
        <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center text-white text-[10px] font-semibold shrink-0 overflow-hidden">
          {order.customer?.avatar
            ? <img src={order.customer.avatar} alt="" className="w-full h-full object-cover" />
            : (order.customer?.fullName ?? '?').charAt(0).toUpperCase()}
        </div>
        <span className="text-sm font-medium text-gray-800 truncate flex-1">
          {order.customer?.fullName ?? order.contactPerson ?? '未知客户'}
        </span>
      </div>

      {/* Date */}
      {order.orderStartDate && (
        <div className="flex items-center gap-1 mb-2 text-xs text-gray-400">
          <i className="ri-calendar-line text-xs" />
          {fmtDate(order.orderStartDate)}
          {isMultiDay
            ? ` ~ ${fmtDate(order.orderEndDate)}`
            : order.orderEndDate && order.orderEndDate !== order.orderStartDate
            ? ` ~ ${fmtDate(order.orderEndDate)}`
            : ''}
        </div>
      )}

      {/* 今日到达提醒 */}
      {todayArrival && (
        <div className={`flex items-center gap-1.5 mb-2 px-2 py-1 rounded-lg text-[10px] font-medium ${
          todayArrival.isPast
            ? 'bg-gray-50 text-gray-400'
            : todayArrival.minutesUntil <= 30
            ? 'bg-rose-50 text-rose-600'
            : 'bg-amber-50 text-amber-600'
        }`}>
          <i className={`ri-map-pin-time-line text-xs`} />
          今日到达 {todayArrival.timeStr}
          {!todayArrival.isPast && todayArrival.minutesUntil <= 60 && (
            <span className={`ml-auto flex items-center gap-0.5 ${todayArrival.minutesUntil <= 30 ? 'text-rose-500' : 'text-amber-500'}`}>
              <span className={`w-1 h-1 rounded-full animate-pulse ${todayArrival.minutesUntil <= 30 ? 'bg-rose-400' : 'bg-amber-400'}`} />
              {todayArrival.minutesUntil <= 0 ? '即将' : `${todayArrival.minutesUntil}分钟`}
            </span>
          )}
          {todayArrival.isPast && <span className="ml-auto">已过</span>}
        </div>
      )}

      {/* Contact person - only shown when filled */}
      {order.contactPerson && (
        <div className="flex items-center gap-1 mb-2 text-xs text-gray-400">
          <i className="ri-user-2-line text-xs" />
          <span className="truncate">对接：{order.contactPerson}</span>
        </div>
      )}

      {/* Activity address - only shown when filled */}
      {order.activityAddress && (
        <div className="flex items-center gap-1 mb-2 text-xs text-gray-400">
          <i className="ri-map-pin-line text-xs" />
          <span className="truncate" title={order.activityAddress}>{order.activityAddress}</span>
        </div>
      )}

      {/* Amount */}
      {order.orderAmount != null && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-50">
          <span className="text-sm font-bold text-gray-800">
            ¥{totalAmt.toLocaleString('zh-CN', { minimumFractionDigits: 0 })}
          </span>
          {/* 0 元占位单不出付款徽章：它没收款义务，落到「已结清」那一档等于把它算进收齐 */}
          {!noPaymentObligation && (
            balance > 0 ? (
              <span className="text-[10px] text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-full">
                余 ¥{balance.toLocaleString('zh-CN', { minimumFractionDigits: 0 })}
              </span>
            ) : (
              <span className="text-[10px] text-teal-500 bg-teal-50 px-1.5 py-0.5 rounded-full">
                已结清
              </span>
            )
          )}
        </div>
      )}
    </div>
  );
}

// ── Draggable card wrapper ──────────────────────────────────────────
function DraggableCard({ order, onCardClick }: { order: OrderItem; onCardClick: () => void }) {
  // 拖动 = PATCH /orders/:id/status，服务端只认建单方（归档对终态单仍开放，取消要在途）。
  // 所以这里只按「是不是自己创建的」禁拖，能不能落到某一列由 handleDragEnd 逐列判。
  const canDrag = order.isOwner === true;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.id, disabled: !canDrag });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ opacity: isDragging ? 0.3 : 1 }}
      title={canDrag ? undefined : '只有你自己创建的订单能改状态'}
    >
      <KanbanCard order={order} onClick={onCardClick} />
    </div>
  );
}

// ── Droppable column ────────────────────────────────────────────────
function DroppableColumn({
  column,
  onCardClick,
}: {
  column: KanbanColumn;
  onCardClick: (order: OrderItem) => void;
}) {
  // 未执行 / 执行中 / 已完成 由后端按订单时间自算，不是可写入的目标状态，
  // 禁用为放置区（dnd-kit 的 disabled droppable 不参与碰撞检测），避免拖过去后 400 静默失败
  const writable = ORDER_STATUSES_WRITABLE.includes(column.status);
  const { setNodeRef, isOver } = useDroppable({ id: column.status, disabled: !writable });
  const style = COLUMN_STYLES[column.status] ?? COLUMN_STYLES['已归档'];

  return (
    <div className="flex flex-col min-w-[264px] w-[264px] shrink-0">
      {/* Column header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 rounded-t-xl border-x border-t ${style.header}`}>
        <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
        <span className="text-sm font-semibold text-gray-700">{column.status}</span>
        {!writable && (
          <span
            title="该状态由系统按订单起止时间自动判定，不可手动拖入"
            className="flex items-center gap-0.5 text-[10px] font-medium text-gray-400 cursor-help"
          >
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line" /></div>
            自动
          </span>
        )}
        <span className={`ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${style.count}`}>
          {column.orders.length}
        </span>
      </div>

      {/* Cards area */}
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-[60vh] rounded-b-xl border-x border-b p-2.5 space-y-2.5 overflow-y-auto transition-colors ${
          isOver
            ? 'bg-teal-50/60 border-teal-300'
            : 'bg-gray-50/60 border-gray-100'
        }`}
        style={{ maxHeight: 'calc(100vh - 260px)' }}
      >
        {column.orders.map(order => (
          <DraggableCard
            key={order.id}
            order={order}
            onCardClick={() => onCardClick(order)}
          />
        ))}
        {column.orders.length === 0 && (
          <div className={`flex flex-col items-center justify-center py-12 text-center transition-colors ${isOver ? 'opacity-70' : 'opacity-40'}`}>
            <i className={`${writable ? 'ri-inbox-line' : 'ri-time-line'} text-2xl text-gray-300 mb-1.5`} />
            <p className="text-xs text-gray-300">{writable ? '拖入此处' : '系统按时间自动判定'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 拖进某一列会被服务端拒时，这里先给出同一句原因（返回空串表示放行）。
 *
 * 两条判据分别来自 `orders.service#update`：归档是终态单保留的后续动作，建单方即可；
 * 取消要整单可写（`canEditOrder`，即档期还在未执行/执行中），已完成的单不许再取消。
 * 目标列只会是这两列——其余三列是 disabled droppable，落不进去。
 */
function statusMoveRefusal(order: OrderItem, newStatus: string): string {
  if (order.isOwner !== true) return '只能改你自己创建的订单的状态';
  if (newStatus === '已取消' && order.canEditOrder !== true) {
    return '这张单当前不可取消（档期已过或已进终态），需要的话可以直接归档';
  }
  return '';
}

// ── Main kanban board ───────────────────────────────────────────────
export default function OrderKanbanView({ columns, loading, error, onMoveOrder, onCardClick }: Props) {
  const [activeOrder, setActiveOrder] = useState<OrderItem | null>(null);
  const [moveHint, setMoveHint] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  const handleDragStart = (event: DragStartEvent) => {
    setMoveHint('');
    const id = String(event.active.id);
    for (const col of columns) {
      const found = col.orders.find(o => o.id === id);
      if (found) { setActiveOrder(found); break; }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveOrder(null);
    const { active, over } = event;
    if (!over) return;
    const orderId = String(active.id);
    const newStatus = String(over.id);
    // Find current status
    for (const col of columns) {
      const order = col.orders.find(o => o.id === orderId);
      if (!order) continue;
      if (col.status !== newStatus) {
        const refusal = statusMoveRefusal(order, newStatus);
        if (refusal) {
          setMoveHint(refusal);
          break;
        }
        // 放行后仍可能被服务端拒（比如档期刚好过完）：失败原文必须落在这里，
        // 否则卡片自己弹回去，用户只会以为自己没拖准。
        Promise.resolve(onMoveOrder(orderId, newStatus)).catch((e: unknown) => {
          setMoveHint(e instanceof Error ? e.message : '状态修改失败，请稍后重试');
        });
      }
      break;
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-4">
        {ORDER_STATUSES.map(s => (
          <div key={s} className="min-w-[264px] w-[264px] shrink-0">
            <div className="h-10 bg-gray-100 rounded-t-xl animate-pulse" />
            <div className="bg-gray-50 rounded-b-xl border border-gray-100 p-2.5 space-y-2.5 min-h-[200px]">
              {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse" />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-600">
        {error}
      </div>
    );
  }

  return (
    <>
      {moveHint && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
          <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-px"><i className="ri-error-warning-line text-xs" /></div>
          <span className="flex-1">{moveHint}</span>
          <button onClick={() => setMoveHint('')}
            className="w-4 h-4 flex items-center justify-center text-amber-400 hover:text-amber-600 cursor-pointer shrink-0">
            <i className="ri-close-line text-xs" />
          </button>
        </div>
      )}
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4 items-start">
          {columns.map(col => (
            <DroppableColumn key={col.status} column={col} onCardClick={onCardClick} />
          ))}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeOrder ? (
            <div className="rotate-2 opacity-90">
              <KanbanCard order={activeOrder} isDragging />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
